import React from 'react';
import { Clock, AlertTriangle, Calendar } from 'lucide-react';

const PointsExpirationInfo = ({ pointsToExpire = [], expiredPoints = [], hasExpirationInfo = true, error = null, className = '' }) => {
  // Se não há informações de expiração disponíveis
  if (!hasExpirationInfo) {
    return (
      <div className={`space-y-4 ${className}`}>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Calendar className="text-blue-600 mr-2 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 mb-2">
                Informações de Expiração
              </h4>
              <p className="text-sm text-blue-700">
                As informações detalhadas sobre expiração de pontos não estão disponíveis na consulta pública.
                Para ver todos os detalhes, acesse sua conta no painel administrativo da empresa.
              </p>
              {error && (
                <p className="text-xs text-blue-600 mt-2 italic">
                  {error}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
  // Agrupar pontos por data de expiração próxima (próximos 30 dias)
  const getPointsExpiringSoon = () => {
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    
    return pointsToExpire.filter(entry => 
      entry.expiresAt <= thirtyDaysFromNow
    );
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (date) => {
    return date.toLocaleString('pt-BR');
  };

  const getDaysUntilExpiration = (expirationDate) => {
    const today = new Date();
    const diffTime = expirationDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const pointsExpiringSoon = getPointsExpiringSoon();
  const totalPointsExpiringSoon = pointsExpiringSoon.reduce((sum, entry) => sum + entry.points, 0);
  const totalExpiredPoints = expiredPoints.reduce((sum, entry) => sum + entry.points, 0);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Pontos que vão expirar em breve */}
      {pointsExpiringSoon.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="text-yellow-600 mr-2 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-800 mb-2">
                Pontos que expiram em breve
              </h4>
              <p className="text-sm text-yellow-700 mb-3">
                Você tem <strong>{totalPointsExpiringSoon} pontos</strong> que expiram nos próximos 30 dias.
              </p>
              
              <div className="space-y-2">
                {pointsExpiringSoon.slice(0, 3).map((entry, index) => {
                  const daysLeft = getDaysUntilExpiration(entry.expiresAt);
                  return (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-yellow-700">
                        {entry.points} pontos - {entry.reason}
                      </span>
                      <span className="font-medium text-yellow-800">
                        {daysLeft > 0 ? `${daysLeft} dias` : 'Hoje'}
                      </span>
                    </div>
                  );
                })}
                
                {pointsExpiringSoon.length > 3 && (
                  <p className="text-xs text-yellow-600">
                    + {pointsExpiringSoon.length - 3} outras entradas...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Todos os pontos com expiração */}
      {pointsToExpire.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Clock className="text-blue-600 mr-2 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-medium text-blue-800 mb-2">
                Cronograma de Expiração
              </h4>
              
              <div className="max-h-32 overflow-y-auto space-y-1">
                {pointsToExpire.map((entry, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-blue-700">
                      {entry.points} pontos
                    </span>
                    <span className="text-blue-600">
                      {formatDate(entry.expiresAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pontos já expirados */}
      {expiredPoints.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <Calendar className="text-red-600 mr-2 mt-0.5" size={20} />
            <div className="flex-1">
              <h4 className="font-medium text-red-800 mb-2">
                Pontos Expirados
              </h4>
              <p className="text-sm text-red-700 mb-3">
                Total de <strong>{totalExpiredPoints} pontos</strong> expirados.
              </p>
              
              <div className="max-h-24 overflow-y-auto space-y-1">
                {expiredPoints.slice(0, 3).map((entry, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-red-700">
                      {entry.points} pontos - {entry.reason}
                    </span>
                    <span className="text-red-600 text-xs">
                      {formatDate(entry.expiresAt)}
                    </span>
                  </div>
                ))}
                
                {expiredPoints.length > 3 && (
                  <p className="text-xs text-red-600">
                    + {expiredPoints.length - 3} outras expiracões...
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Caso não haja pontos com expiração */}
      {pointsToExpire.length === 0 && expiredPoints.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center">
            <Clock className="text-gray-400 mr-2" size={20} />
            <span className="text-gray-600 text-sm">
              Seus pontos não possuem data de expiração configurada.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsExpirationInfo;
